import { CreateShakaPackagerJobsResult } from "../../../domain/media-transcoder/create-shaka-packager-jobs-result";
import { CreateShakaPackagerJobsBulkResult } from "../../../domain/media-transcoder/create-shaka-packager-jobs-bulk-result";
import { ShakaPackagerEligibleMedia } from "../../../domain/media-transcoder/shaka-packager-eligible-media";
import { CreateShakaPackagerJobsByMediaResult } from "../../../domain/media-transcoder/create-shaka-packager-jobs-by-media-result";
import { IMediaTranscoderRepository } from "../../../domain/media-transcoder/media-transcoder.repository";
import { TranscodingProfile } from "../../../domain/media-transcoder/transcoding-profile";
import {
  ETranscodingDestination,
  ETranscodingJobPriority,
  ETranscodingJobStatus,
  TranscoderJobDetails,
} from "../../../domain/media-transcoder/transcoder-job";
import { Injectable } from "@angular/core";
import { HttpService } from "../../http/http";
import moment from "moment";
import {
  EMediaTranscodingItemStatus,
  TranscodingItem,
} from "../../../domain/media-transcoder/transcoding-item";
import { TranscodingDestination } from "../../../domain/media-transcoder/transcoding-destination";
import { MediaTranscodingItem } from "../../../domain/media-transcoder/media-transcoding-item";
import { ContentControlTranscodingItem } from "../../../domain/media-transcoder/content-control-transcoding-item";
import { TranscodingSideEffect } from "app/js/src/domain/media-transcoder/Transcoding-side-effect";
import {
  EShakaPackagerJobStatus,
  ShakaPackagerJob,
} from "app/js/src/domain/media-transcoder/shaka-packager-job";

@Injectable()
export class MediaTranscoderRepository implements IMediaTranscoderRepository {
  private _cachedTranscodingProfiles: TranscodingProfile[] = null;
  private _cachedTranscodingDestinations: TranscodingDestination[] = null;
  private _cachedTranscodingSideEffects: TranscodingSideEffect[] = null;
  constructor(private _httpService: HttpService) {}

  getProfiles(): Promise<TranscodingProfile[]> {
    if (this._cachedTranscodingProfiles !== null) {
      return Promise.resolve(this._cachedTranscodingProfiles);
    }
    return this._httpService
      .get(`MediaTranscoder/TenantTranscodingProfiles`)
      .then((profilesResponse) =>
        profilesResponse.map(
          (p) => new TranscodingProfile(p.Id, p.Name, p.IsClustered),
        ),
      )
      .then((result) => {
        this._cachedTranscodingProfiles = result;
        return result;
      });
  }

  async getJobs(
    statuses: ETranscodingJobStatus[],
  ): Promise<TranscoderJobDetails[]> {
    const response = await this._httpService.get(
      `MediaTranscoder/Jobs?Statuses=${statuses
        .map((s) => s.toString())
        .join("|")}`,
    );
    console.log("media-transcoder-repository->response", response);
    const returnValue = response.map((responseJob: any) => {
      const job = new TranscoderJobDetails(
        responseJob.id,
        responseJob.name,
        responseJob.destinationId,
        responseJob.priority as ETranscodingJobPriority,
        responseJob.status as ETranscodingJobStatus,
        moment.utc(responseJob['estimatedStartTime']),
        moment.utc(responseJob['estimatedEndTime']),
        moment.utc(
          responseJob['actualStartTime'] ?? responseJob['estimatedStartTime'],
        ),
        moment.utc(
          responseJob['actualEndTime'] ?? responseJob['estimatedEndTime'],
        ),
        this._mapTranscodingItemsFromJobsResponse(
          responseJob.transcodingItems ?? responseJob.TranscodingItems,
        ),
        responseJob.transcodingProfileId,
        responseJob.sideEffectId ?? responseJob.SideEffectId ?? 0,
      );
      job.itemStatuses = (
        responseJob.transcodingItemStatuses ??
        responseJob.TranscodingItemStatuses ??
        []
      ).map((status: number) => status as EMediaTranscodingItemStatus);
      return job;
    });
    console.log("media-transcoder-repository->result", returnValue);
    return returnValue;
  }
  getSideEffects(): Promise<TranscodingSideEffect[]> {
    if (this._cachedTranscodingSideEffects !== null) {
      return Promise.resolve(this._cachedTranscodingSideEffects);
    }
    return this._httpService
      .get(`MediaTranscoder/TenantTranscodingSideEffects`)
      .then((result) => {
        if (result.length > 0) {
          return result.map((r) => new TranscodingSideEffect(r.Id, r.Name));
        }
        return [];
      })
      .then((result) => {
        this._cachedTranscodingSideEffects = result;
        return result;
      });
  }

  createNewJob(newJob: TranscoderJobDetails): Promise<void> {
    const trnascodingItemsRequest = newJob.transcodingItems.map((item) =>
      item.asRequest(),
    );
    return this._httpService.post(`MediaTranscoder/NewJob`, {
      Id: newJob.id,
      Name: newJob.name,
      DestinationId: newJob.destinationId,
      PriorityId: newJob.priority,
      ProfileId: newJob.transcodingProfileId,
      SideEffectId: newJob.sideEffectId,
      TranscodingItems: trnascodingItemsRequest,
      ExecuteOnlySideEffects: newJob.executeOnlySideEffects,
    });
  }

  getTranscodingJobDetails(id: string): Promise<TranscoderJobDetails> {
    return this._httpService.get(`MediaTranscoder/JobDetails?jobId=${id}`).then(
      (responseJob) =>
        new TranscoderJobDetails(
          responseJob.id,
          responseJob.name,
          responseJob.destinationId,
          responseJob.priority as ETranscodingJobPriority,
          responseJob.status as ETranscodingJobStatus,
          moment.utc(responseJob["estimatedStartTime"]),
          moment.utc(responseJob["estimatedEndTime"]),
          moment.utc(responseJob["actualStartTime"]),
          moment.utc(responseJob["actualEndTime"]),

          responseJob.transcodingItems.map(
            (transcdingItemResponse) =>
              new MediaTranscodingItem(
                transcdingItemResponse.id,
                transcdingItemResponse.mediaId,
                transcdingItemResponse.videoTitle,
                transcdingItemResponse.videoTenantUniqueId,
                transcdingItemResponse.videoDurationMs,
                // tslint:disable-next-line:max-line-length
                transcdingItemResponse.startTimeMs,
                transcdingItemResponse.endTimeMs,
                transcdingItemResponse.status as EMediaTranscodingItemStatus,
                transcdingItemResponse.statusesLogs,
                moment.utc(transcdingItemResponse.lastStatusTime),
                transcdingItemResponse.resultUrl,
              ),
          ),
          responseJob.transcodingProfileId,
          responseJob.sideEffectId ?? responseJob.SideEffectId ?? 0,
          responseJob.executeOnlySideEffects ??
            responseJob.ExecuteOnlySideEffects,
        ),
    );
  }

  private _mapTranscodingItemsFromJobsResponse(
    items: Record<string, unknown>[] | undefined,
  ): TranscodingItem[] {
    if (!items?.length) {
      return [];
    }

    return items.map((item) => {
      const contentControlItemUid =
        (item.contentControlItemUid as string | undefined) ??
        (item.ContentControlItemUid as string | undefined);
      const startTimeMs = Number(item.startTimeMs ?? item.StartTimeMs ?? 0);
      const endTimeMs = Number(item.endTimeMs ?? item.EndTimeMs ?? 0);
      const status = Number(
        item.status ?? item.Status ?? EMediaTranscodingItemStatus.Pending,
      ) as EMediaTranscodingItemStatus;

      if (contentControlItemUid) {
        return new ContentControlTranscodingItem(
          String(item.id ?? item.Id),
          contentControlItemUid,
          "",
          "",
          0,
          startTimeMs,
          endTimeMs,
          status,
          "",
          null,
          "",
        );
      }

      return new MediaTranscodingItem(
        String(item.id ?? item.Id),
        Number(item.mediaId ?? item.MediaId),
        "",
        "",
        0,
        startTimeMs,
        endTimeMs,
        status,
        "",
        null,
        "",
      );
    });
  }

  getTenantTranscodingDestinations(): Promise<TranscodingDestination[]> {
    if (this._cachedTranscodingDestinations !== null) {
      return Promise.resolve(this._cachedTranscodingDestinations);
    }
    return this._httpService
      .get(`MediaTranscoder/TenantTranscodingDestinations`)
      .then((result) =>
        result.map(
          (r) => new TranscodingDestination(r.id, r.name, r.uiSelected),
        ),
      )
      .then((result) => {
        this._cachedTranscodingDestinations = result;
        return result;
      });
  }

  abortJob(jobId: string): Promise<void> {
    return this._httpService.post(`MediaTranscoder/AbortJob/${jobId}`, null);
  }

  changeJobPriority(
    jobId: string,
    newPriority: ETranscodingJobPriority,
  ): Promise<void> {
    return this._httpService.post(
      `MediaTranscoder/Job/${jobId}/ChangePriority?newPriority=${newPriority}`,
      null,
    );
  }

  getShakaPackagerJobs(): Promise<ShakaPackagerJob[]> {
    return this._httpService
      .get(`MediaTranscoder/ShakaPackager/Jobs`)
      .then((rows) =>
        (rows ?? []).map((row: Record<string, unknown>) => {
          const jobUid = (row.jobUid ?? row.JobUid) as string;
          const mediaId = Number(row.mediaId ?? row.MediaId);
          const mediaName = (row.mediaName ?? row.MediaName ?? "") as string;
          const createdAt = row.createdAt ?? row.CreatedAt;
          const lastUpdate = row.lastJobStatusUpdate ?? row.LastJobStatusUpdate;
          const jobStatus = this._parseShakaPackagerJobStatus(
            row.jobStatus ?? row.JobStatus,
          );
          const sourcePath = (row.sourcePath ?? row.SourcePath ?? "") as string;
          const logPath = (row.jobProcessLogPath ??
            row.JobProcessLogPath ??
            null) as string | null;
          const fullMode = Boolean(row.fullMode ?? row.FullMode ?? true);

          return new ShakaPackagerJob(
            jobUid,
            mediaId,
            mediaName,
            moment.utc(createdAt as string),
            jobStatus as EShakaPackagerJobStatus,
            moment.utc(lastUpdate as string),
            sourcePath,
            logPath,
            fullMode,
          );
        }),
      );
  }

  getShakaPackagerProcessLog(
    jobUid: string,
  ): Promise<Record<string, unknown>> {
    return this._httpService.get(
      `MediaTranscoder/ShakaPackager/ProcessLog?jobUid=${jobUid}`,
    );
  }

  createShakaPackagerJobs(
    transcodingJobId: string,
    fullMode = true,
  ): Promise<CreateShakaPackagerJobsResult> {
    return this._httpService
      .post(`MediaTranscoder/ShakaPackager/Jobs`, {
        TranscodingJobId: transcodingJobId,
        FullMode: fullMode,
      })
      .then((response) => ({
        created: (response?.created ?? response?.Created ?? []).map(
          (item: Record<string, unknown>) => ({
            jobUid: String(item.jobUid ?? item.JobUid ?? ''),
            mediaId: Number(item.mediaId ?? item.MediaId ?? 0),
            sourcePath: String(item.sourcePath ?? item.SourcePath ?? ''),
          }),
        ),
        skipped: (response?.skipped ?? response?.Skipped ?? []).map(
          (item: Record<string, unknown>) => ({
            mediaId: Number(item.mediaId ?? item.MediaId ?? 0),
            reason: String(item.reason ?? item.Reason ?? ''),
          }),
        ),
      }));
  }

  createShakaPackagerJobsBulk(
    transcodingJobIds: string[],
    fullMode = true,
  ): Promise<CreateShakaPackagerJobsBulkResult> {
    return this._httpService
      .post(`MediaTranscoder/ShakaPackager/Jobs/Bulk`, {
        TranscodingJobIds: transcodingJobIds,
        FullMode: fullMode,
      })
      .then((response) => this._mapShakaPackagerBulkCreateResponse(response));
  }

  private _mapShakaPackagerBulkCreateResponse(
    response: Record<string, unknown>,
  ): CreateShakaPackagerJobsBulkResult {
    const summaryRaw = (response?.summary ?? response?.Summary ?? {}) as Record<
      string,
      unknown
    >;
    const jobsRaw = (response?.jobs ?? response?.Jobs ?? []) as Array<
      Record<string, unknown>
    >;

    return {
      summary: {
        transcodingJobsProcessed: Number(
          summaryRaw.transcodingJobsProcessed ??
            summaryRaw.TranscodingJobsProcessed ??
            0,
        ),
        transcodingJobsFailed: Number(
          summaryRaw.transcodingJobsFailed ??
            summaryRaw.TranscodingJobsFailed ??
            0,
        ),
        packagerJobsCreated: Number(
          summaryRaw.packagerJobsCreated ??
            summaryRaw.PackagerJobsCreated ??
            0,
        ),
        mediaSkipped: Number(
          summaryRaw.mediaSkipped ?? summaryRaw.MediaSkipped ?? 0,
        ),
      },
      jobs: jobsRaw.map((job) => ({
        transcodingJobId: String(
          job.transcodingJobId ?? job.TranscodingJobId ?? '',
        ),
        failure: (job.failure ?? job.Failure ?? null) as string | null,
        created: ((job.created ?? job.Created ?? []) as Array<
          Record<string, unknown>
        >).map((item) => ({
          jobUid: String(item.jobUid ?? item.JobUid ?? ''),
          mediaId: Number(item.mediaId ?? item.MediaId ?? 0),
          sourcePath: String(item.sourcePath ?? item.SourcePath ?? ''),
        })),
        skipped: ((job.skipped ?? job.Skipped ?? []) as Array<
          Record<string, unknown>
        >).map((item) => ({
          mediaId: Number(item.mediaId ?? item.MediaId ?? 0),
          reason: String(item.reason ?? item.Reason ?? ''),
        })),
      })),
    };
  }

  getShakaPackagerEligibleMedia(): Promise<ShakaPackagerEligibleMedia[]> {
    return this._httpService
      .get(`MediaTranscoder/ShakaPackager/EligibleMedia`)
      .then((rows) =>
        (rows ?? []).map((row: Record<string, unknown>) => ({
          mediaId: Number(row.mediaId ?? row.MediaId ?? 0),
          mediaName: String(row.mediaName ?? row.MediaName ?? ''),
          sourcePath: String(row.sourcePath ?? row.SourcePath ?? ''),
          transcodingJobUid: String(
            row.transcodingJobUid ?? row.TranscodingJobUid ?? '',
          ),
          jobCount: Number(row.jobCount ?? row.JobCount ?? 0),
          earliestStartTime: moment.utc(
            (row.earliestStartTime ?? row.EarliestStartTime) as string,
          ),
          latestStartTime: moment.utc(
            (row.latestStartTime ?? row.LatestStartTime) as string,
          ),
          existingCount: Number(row.existingCount ?? row.ExistingCount ?? 0),
          existingStatus: (row.existingStatus ?? row.ExistingStatus ?? null) as
            | string
            | null,
          existingFullMode: (() => {
            const raw = row.existingFullMode ?? row.ExistingFullMode;
            return raw == null ? null : Boolean(raw);
          })(),
          existingPackagerCreatedAt: this._parseOptionalUtcMoment(
            row.existingPackagerCreatedAt ?? row.ExistingPackagerCreatedAt,
          ),
          existingPackagerLastUpdateAt: this._parseOptionalUtcMoment(
            row.existingPackagerLastUpdateAt ?? row.ExistingPackagerLastUpdateAt,
          ),
        })),
      );
  }

  createShakaPackagerJobsByMedia(
    items: { mediaId: number; fullMode: boolean }[],
    options?: { rescheduleExisting?: boolean },
  ): Promise<CreateShakaPackagerJobsByMediaResult> {
    return this._httpService
      .post(`MediaTranscoder/ShakaPackager/Jobs/ByMedia`, {
        Items: items.map((item) => ({
          MediaId: item.mediaId,
          FullMode: item.fullMode,
        })),
        RescheduleExisting: options?.rescheduleExisting ?? false,
      })
      .then((response) => {
        const summaryRaw = (response?.summary ?? response?.Summary ?? {}) as Record<
          string,
          unknown
        >;
        const itemsRaw = (response?.items ?? response?.Items ?? []) as Array<
          Record<string, unknown>
        >;
        return {
          summary: {
            created: Number(summaryRaw.created ?? summaryRaw.Created ?? 0),
            rescheduled: Number(
              summaryRaw.rescheduled ?? summaryRaw.Rescheduled ?? 0,
            ),
            skipped: Number(summaryRaw.skipped ?? summaryRaw.Skipped ?? 0),
            notEligible: Number(
              summaryRaw.notEligible ?? summaryRaw.NotEligible ?? 0,
            ),
          },
          items: itemsRaw.map((item) => ({
            mediaId: Number(item.mediaId ?? item.MediaId ?? 0),
            outcome: String(item.outcome ?? item.Outcome ?? ''),
            fullMode: Boolean(item.fullMode ?? item.FullMode ?? true),
            jobUid: (item.jobUid ?? item.JobUid ?? null) as string | null,
          })),
        };
      });
  }

  private _parseOptionalUtcMoment(raw: unknown): moment.Moment | null {
    if (raw === null || raw === undefined || raw === '') {
      return null;
    }
    const parsed = moment.utc(String(raw));
    return parsed.isValid() ? parsed : null;
  }

  private _parseShakaPackagerJobStatus(
    raw: unknown,
  ): EShakaPackagerJobStatus {
    if (typeof raw === 'number') {
      return raw as EShakaPackagerJobStatus;
    }

    if (typeof raw === 'string') {
      const fromName =
        EShakaPackagerJobStatus[raw as keyof typeof EShakaPackagerJobStatus];
      if (typeof fromName === 'number') {
        return fromName;
      }

      const parsed = Number(raw);
      if (!Number.isNaN(parsed)) {
        return parsed as EShakaPackagerJobStatus;
      }
    }

    return EShakaPackagerJobStatus.Ready;
  }
}
